import type { DocumentType } from '@form-factory/schema';
import type { EvaluationTestCase } from '@form-factory/schema/contracts';
import { Trans, useLingui } from '@lingui/react/macro';
import { Dialog, Button } from '@utima/ui';
import { CopyCheckIcon, Copy } from 'lucide-react';
import { useCallback, useState } from 'react';

import { SimpleTooltip } from '@/components';
import { trpc } from '@/services/trpc';

interface GenerateTestCasesDialogProps {
  document: DocumentType | null;
}

export function GenerateTestCasesDialog({
  document,
}: GenerateTestCasesDialogProps) {
  const { t } = useLingui();
  const [isCopied, setIsCopied] = useState(false);
  const [testCases, setTestCases] = useState<EvaluationTestCase[]>([]);
  const generateTestCasesMutation = trpc.evaluation.generate.useMutation({
    onSuccess: (data: any) => {
      setTestCases(data.testCases);
    },
  });

  const handleGenerateTestCases = () => {
    if (document) {
      generateTestCasesMutation.mutate({
        documentId: document.id,
      });
    }
  };

  const copyToClipboard = useCallback(() => {
    navigator.clipboard
      .writeText(JSON.stringify(testCases, null, 2))
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      });
  }, [testCases]);

  return (
    <Dialog.Content className='flex max-h-[95%] w-full max-w-[1200px] flex-col'>
      <Dialog.Title>
        <Trans>Generate test cases</Trans>
      </Dialog.Title>
      <Dialog.Description>
        <Trans>Generate test cases for this source document.</Trans>
      </Dialog.Description>
      {testCases.length > 0 && (
        <div className='relative size-full overflow-auto'>
          <div className='fixed bottom-24 right-12'>
            <SimpleTooltip title={t`Copy to clipboard`} delayDuration={0}>
              <Button
                onClick={copyToClipboard}
                variant='primary'
                size='icon-xs'
              >
                {isCopied ? (
                  <CopyCheckIcon className='size-4' />
                ) : (
                  <Copy className='size-4' />
                )}
              </Button>
            </SimpleTooltip>
          </div>
          <pre className='overflow-auto whitespace-pre-wrap font-mono text-xs'>
            {JSON.stringify(testCases, null, 2)}
          </pre>
        </div>
      )}
      <Dialog.Footer>
        <Button
          onClick={handleGenerateTestCases}
          disabled={generateTestCasesMutation.isPending || !document}
          loading={generateTestCasesMutation.isPending}
        >
          <Trans>Generate</Trans>
        </Button>
        <Dialog.Close asChild>
          <Button variant='ghost' outline>
            <Trans>Close</Trans>
          </Button>
        </Dialog.Close>
      </Dialog.Footer>
    </Dialog.Content>
  );
}
